import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Modal, Popconfirm, Select } from 'antd'
import { useState } from 'react'
import type { DeliveryReconOverviewRow } from '../types'
import {
  type CHANGE_BAR_ACTIONS,
  getFormattedTimeVariance,
  getShouldDisableActionsDropdown,
  getTotalVariance,
  getTotalVolume,
  type REPORT_ACTIONS,
} from '../util'
import { groupBy } from 'lodash'
import { ExclamationCircleFilled } from '@ant-design/icons'

const { confirm } = Modal

type ChangeBarProps = {
  selected: DeliveryReconOverviewRow[]
  onCancel: () => void
  actions: Record<(typeof CHANGE_BAR_ACTIONS)[number], (items: DeliveryReconOverviewRow[]) => Promise<void>>
}

export function ChangeBar({ selected, onCancel, actions }: ChangeBarProps) {
  const [action, setAction] = useState<(typeof REPORT_ACTIONS)[number] | null>(null)

  const showConfirm = () => {
    confirm({
      title: 'Confirm Void?',
      icon: <ExclamationCircleFilled style={{ color: 'var(--theme-error)' }} />,
      content: 'This action cannot be undone.',
      okText: 'Void',
      okButtonProps: {
        danger: true,
      },
      onOk() {
        onConfirm()
      },
    })
  }

  async function onConfirm() {
    switch (action) {
      case 'Match':
        await actions.Match(selected)
        break
      case 'Approve':
        await actions.Approve(selected)
        break
      case 'Unmatch':
        await actions.Unmatch(selected)
        break
      case 'Void':
        await actions.Void(selected)
        break
    }
    setAction(null)
  }

  const products = groupBy(
    selected.flatMap((s) => s.details),
    'source_product'
  )

  return (
    <Horizontal
      className='change-bar px-4'
      verticalCenter
      borderRadius='4px 4px 0 0'
      width='100%'
      justifyContent='space-between'
    >
      <Horizontal verticalCenter gap='2em' flex={5}>
        <Texto>
          <span style={{ color: 'var(--theme-color-2)', fontWeight: 'bold' }}>{selected.length} Item(s)</span> selected
        </Texto>
        <Horizontal verticalCenter gap='6px'>
          <Texto>Action</Texto>
          <Select
            placeholder='Select'
            size='small'
            style={{ width: 158 }}
            options={Object.keys(actions).map((opt) => {
              return { label: opt, value: opt, disabled: getShouldDisableActionsDropdown(selected)[opt] }
            })}
            value={action}
            onChange={(val) => setAction(val)}
          />
        </Horizontal>
      </Horizontal>
      <Horizontal verticalCenter gap={12} flex={4}>
        <Vertical className='my-2'>
          <Horizontal>
            {['Product', 'BOL Vol.', 'Measured Vol.', 'Time Variance', 'Vol. Variance'].map((label, i) => (
              <Texto key={label} category='label' className={`${i === 0 ? 'flex-2' : 'flex-1'} text-ellipsis`}>
                {label}
              </Texto>
            ))}
          </Horizontal>
          {Object.entries(products).map(([product, details]) => (
            <Horizontal key={product}>
              <Texto className='flex-2 text-ellipsis'>{product}</Texto>
              <Texto className='flex-1 text-ellipsis'>{addCommasToNumber(getTotalVolume(details, 'bol'))}</Texto>
              <Texto className='flex-1 text-ellipsis'>{addCommasToNumber(getTotalVolume(details, 'delivery'))}</Texto>
              <Texto className='flex-1 text-ellipsis'>{getFormattedTimeVariance(details)}</Texto>
              <Texto className='flex-1 text-ellipsis'>{addCommasToNumber(getTotalVariance(details))}</Texto>
            </Horizontal>
          ))}
        </Vertical>
        <GraviButton onClick={onCancel}>Cancel</GraviButton>
        <GraviButton
          color='primary'
          disabled={!action}
          onClick={() => (action === 'Void' ? showConfirm() : onConfirm())}
        >
          Confirm
        </GraviButton>
      </Horizontal>
    </Horizontal>
  )
}
